import {
    CustomerIntelligenceResponse,
    OrdersSegmentationResponse,
    SegmentationDimension,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

export type RiskLevel = "critical" | "high" | "medium" | "ok" | "unknown";

export type CorridorRowVM = {
    name: string;
    volume: string;
    revenue: string;
    orders: number;
    success: string;
    p95: string;
    takeRate: string;
    risk: RiskLevel;
};

export type ClientRowVM = {
    name: string;
    volume: string;
    revenue: string;
    share: number;
    orders: number;
    success: string;
    growth: string;
};

export type ConcentrationAlertSeverity = "critical" | "warning";

export type ConcentrationVM = {
    top1: number | null;
    top3: number | null;
    top5: number | null;
    nonTopGrowth: string;
    revExTop1: string;
    // null = healthy/diversified (below the warning band) OR no data → no banner.
    // Thresholds mirror the canonical ValueConcentrationSection.tileTone (30/60),
    // reading top1 as a 0–100 revenue-share percentage.
    alertSeverity: ConcentrationAlertSeverity | null;
};

const concentrationAlertSeverity = (top1: number | null): ConcentrationAlertSeverity | null => {
    if (top1 === null) return null;
    if (top1 >= 60) return "critical";
    if (top1 >= 30) return "warning";
    return null;
};

export type CorridorsVM = {
    corridors: CorridorRowVM[];
    clients: ClientRowVM[];
    concentration: ConcentrationVM;
    // PROVIDER-PII: withdrawProvider/onrampProvider segmentation values are raw
    // vendor names in the index (e.g. "nilos") — never expose vendor names in
    // an external-facing surface, so this section always empty-states.
    providersEmpty: true;
};

const MUTED_VALUE = "—";

type FromDataInput = {
    segmentation: Partial<Record<SegmentationDimension, OrdersSegmentationResponse>>;
    customerIntelligence: CustomerIntelligenceResponse | null;
};

const derivedTakeRatePercentage = (revenue: number, volume: number): number | null =>
    volume > 0 ? (revenue / volume) * 100 : null;

// 85/90/95 are product-chosen display buckets for mapping a real success
// rate into a risk chip — presentation thresholds, not backend data.
const riskFromSuccessRate = (successRate: number): RiskLevel => {
    if (successRate < 85) return "critical";
    if (successRate < 90) return "high";
    if (successRate < 95) return "medium";
    return "ok";
};

const fromData = (input: FromDataInput): CorridorsVM => {
    const pairRows = input.segmentation.pair?.rows ?? [];
    const clientRows = input.segmentation.client?.rows ?? [];

    const corridors: CorridorRowVM[] = pairRows.map((row) => {
        const takeRate = derivedTakeRatePercentage(row.businessRevenue, row.volume);
        return {
            name: row.segment,
            volume: formatters.formatCurrency(row.volume),
            revenue: formatters.formatCurrency(row.businessRevenue),
            orders: row.orders,
            success: `${formatters.formatNumber(row.successRate)}%`,
            p95: formatters.formatDuration(row.p95TotalTimeMinutes),
            takeRate: takeRate === null ? MUTED_VALUE : `${takeRate.toFixed(2)}%`,
            risk: riskFromSuccessRate(row.successRate),
        };
    });

    const totalClientRevenue = clientRows.reduce((sum, row) => sum + row.businessRevenue, 0);
    const clients: ClientRowVM[] = clientRows.map((row) => ({
        name: row.segment,
        volume: formatters.formatCurrency(row.volume),
        revenue: formatters.formatCurrency(row.businessRevenue),
        share: totalClientRevenue > 0 ? (row.businessRevenue / totalClientRevenue) * 100 : 0,
        orders: row.orders,
        success: `${formatters.formatNumber(row.successRate)}%`,
        growth: MUTED_VALUE,
    }));

    const tops = input.customerIntelligence?.valueConcentration.tops ?? [];
    const findTop = (topCount: number): number | null =>
        tops.find((top) => top.topCount === topCount)?.revenueShare ?? null;
    const top1 = findTop(1);

    return {
        corridors,
        clients,
        concentration: {
            top1,
            top3: findTop(3),
            top5: findTop(5),
            nonTopGrowth: MUTED_VALUE,
            revExTop1: MUTED_VALUE,
            alertSeverity: concentrationAlertSeverity(top1),
        },
        providersEmpty: true,
    };
};

export const corridorsViewModel = { fromData };
